import React, { useEffect, useState } from "react";
import { ChevronDown, Phone, Menu, X } from "lucide-react";
import content from "@/data/content.json";

const { nav, meta } = content;

export const Header = () => {
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#1e2532] text-white"
      style={{
        boxShadow: scrolled ? "0 1px 0 rgba(255,255,255,0.12)" : "none",
        transition: "box-shadow .4s ease",
      }}
    >
      <div className="mx-auto flex max-w-[1240px] items-center gap-6 px-6 md:px-10 h-[76px]">
        <a href="/" data-testid="header-logo" className="flex shrink-0 flex-col">
          <span className="flex items-start gap-[2px] leading-none">
            <span className="font-display text-[21px] font-bold leading-none tracking-[-0.03em]">energy</span>
            <span className="font-display text-[21px] font-bold leading-none tracking-[-0.03em] text-[#3ecf9b]">
              leads
            </span>
            <span className="mt-[1px] text-[8px] font-bold leading-none text-white/60">TM</span>
          </span>
          <span className="mt-[4px] text-[8.5px] font-semibold uppercase leading-none tracking-[0.13em] text-white/55">
            Mehr Projekte. Mehr Energie.
          </span>
        </a>

        <nav className="hidden lg:flex flex-1 items-center justify-center gap-1" aria-label="Hauptnavigation">
          {nav.map((item, i) =>
            item.items ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpen(i)}
                onMouseLeave={() => setOpen(null)}
              >
                <button
                  data-testid={`nav-dropdown-${i}`}
                  className="flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2 text-[14.5px] font-medium text-white/85 hover:text-white"
                  style={{ transition: "color .3s ease" }}
                >
                  {item.label}
                  <ChevronDown size={14} strokeWidth={2.2} />
                </button>
                {open === i && (
                  <div
                    data-testid={`nav-dropdown-panel-${i}`}
                    className="absolute left-0 top-full w-[320px] border border-white/12 bg-[#1e2532] py-2"
                  >
                    {item.caption && (
                      <p className="border-b border-white/10 px-5 pb-3 pt-2 text-[12.5px] leading-[1.5] text-white/45">
                        {item.caption}
                      </p>
                    )}
                    {item.items.map((sub) => (
                      sub.divider ? (
                        <span key="divider" className="my-2 block border-t border-white/12" aria-hidden="true" />
                      ) : (
                      <a
                        key={sub.label + sub.href}
                        href={sub.href}
                        data-testid={`nav-sub-link-${sub.label}`}
                        className="block px-5 py-2.5 text-[14.5px] text-white/75 hover:bg-white/[0.06] hover:text-white"
                        style={{ transition: "color .25s ease, background-color .25s ease" }}
                      >
                        {sub.label}
                      </a>
                      )
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-link-${item.label}`}
                className="whitespace-nowrap px-3.5 py-2 text-[14.5px] font-medium text-white/85 hover:text-white"
                style={{ transition: "color .3s ease" }}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <div className="ml-auto flex items-center gap-3 sm:gap-5">
          <a
            href={meta.phoneHref}
            data-testid="header-phone"
            className="hidden xl:flex items-center gap-2 whitespace-nowrap text-[14.5px] font-medium text-white/85 hover:text-white"
          >
            <Phone size={15} strokeWidth={2.2} />
            <span className="link-underline">{meta.phone}</span>
          </a>
          <a
            href="#kontakt"
            data-testid="header-cta"
            className="btn-solid inline-flex items-center whitespace-nowrap px-3.5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.06em] sm:px-5 sm:py-3 sm:text-[13px]"
          >
            <span className="sm:hidden">Jetzt anfragen</span>
            <span className="hidden sm:inline">Erstgespräch vereinbaren</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menü"
            className="lg:hidden p-2 -mr-2"
            onClick={() => setMobile((v) => !v)}
          >
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/12 bg-[#1e2532] px-6 pb-8 pt-4">
          {nav.map((item) => (
            <div key={item.label} className="py-2">
              {item.href ? (
                <a href={item.href} className="block py-2 text-[16px] font-medium">
                  {item.label}
                </a>
              ) : (
                <>
                  <p className="py-2 text-[12px] uppercase tracking-[0.18em] text-[#3ecf9b]">{item.label}</p>
                  {item.caption && (
                    <p className="pb-2 text-[13px] leading-[1.5] text-white/45">{item.caption}</p>
                  )}
                  {item.items.map((s) => (
                    s.divider ? null : (
                    <a key={s.label + s.href} href={s.href} className="block py-1.5 pl-1 text-[15px] text-white/80">
                      {s.label}
                    </a>
                    )
                  ))}
                </>
              )}
            </div>
          ))}
          <a href={meta.phoneHref} className="mt-4 block py-2 text-[16px] font-semibold">
            {meta.phone}
          </a>
        </div>
      )}
    </header>
  );
};
